import React, { useState } from "react";
import { Link } from "react-router-dom";
import { KINDS, SECTIONS, countItems } from "./toc";

/**
 * Книга Знаний — оглавление всех материалов о платформе.
 *
 * Максим 22.08.2026 попросил сделать оглавление разделов и разложить статьи
 * по типам. Состав снят с блога в GitBook: девять разделов, около полусотни
 * страниц.
 *
 * Статьи пока открываются в первоисточнике. Это осознанный промежуточный
 * шаг: полный состав Книги виден уже сегодня, а тексты переносятся по мере
 * готовности. Когда статья переедет в кабинет, у неё меняется одна строчка
 * в оглавлении — с внешней ссылки на внутреннюю.
 *
 * Эта же Книга — источник для ИИ-ассистента, поэтому полнота оглавления
 * важнее красоты: чего здесь нет, о том ассистент не расскажет.
 */

const PUBLIC = process.env.PUBLIC_URL || "";

const ExternalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/** «1 статья», «2 статьи», «5 статей». */
const plural = (n) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "статья";
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return "статьи";
  return "статей";
};

const KnowledgePage = () => {
  const [kind, setKind] = useState(null);

  const shown = kind ? SECTIONS.filter((s) => s.kind === kind) : SECTIONS;
  const total = countItems();

  return (
    <div className="fw-page">
      <h1 className="fw-title">Книга Знаний</h1>
      <p className="fw-sub">
        Всё о «Волшебном мире»: как устроена платформа, что дают роли, как
        работают миры и награды. Сейчас в оглавлении {total} {plural(total)} в{" "}
        {SECTIONS.length} разделах.
      </p>

      <div className="fw-worlds-filter" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={kind === null}
          className={`fw-ptab${kind === null ? " active" : ""}`}
          onClick={() => setKind(null)}
        >
          Все разделы
        </button>
        {KINDS.map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={kind === k}
            className={`fw-ptab${kind === k ? " active" : ""}`}
            onClick={() => setKind(k)}
          >
            {k}
          </button>
        ))}
      </div>

      <div className="fw-know-list">
        {shown.map((section) => (
          <section className="fw-card fw-know-sec" key={section.key}>
            <div className="fw-know-head">
              <div>
                <b>{section.title}</b>
                <span className="fw-know-lead">{section.lead}</span>
              </div>
              <span className="fw-know-count">
                {section.items.length} {plural(section.items.length)}
              </span>
            </div>

            <ul className="fw-know-items">
              {section.items.map((item) =>
                item.to ? (
                  <li key={item.title}>
                    <Link to={`${PUBLIC}${item.to}`}>
                      <span>{item.title}</span>
                      <ArrowIcon />
                    </Link>
                  </li>
                ) : (
                  <li key={item.title}>
                    <a href={item.href} target="_blank" rel="noreferrer">
                      <span>{item.title}</span>
                      <ExternalIcon />
                    </a>
                  </li>
                )
              )}
            </ul>
          </section>
        ))}
      </div>

      <p className="fw-sec-sub fw-know-note">
        Статьи со стрелкой уголком открываются в блоге проекта — они ещё не
        перенесены в кабинет. Переносим по разделам; порядок оглавления при
        этом не меняется.
      </p>
    </div>
  );
};

export default KnowledgePage;
